/* The above code is a React component called `OrderCards` that displays information related to an
order. It includes functionality to show a timeline of the order status, general information about
the order, and a list of parts associated with the order. */
import { useContext, useEffect, useMemo, useState } from "react";
import "./ordersCards.css";
import { TimeLineStepper } from "../../../../../components/TimelineStepper/TimeLineStepper";
import { dropdownConfig } from "../../../../../services/dropdownsConfigService";
import { HTTP_RESPONSE_CODES } from "../../../../../globals/globals";
import Dropdowns from "../../../../../components/Dropdowns/Dropdowns";
import Skeleton from "react-loading-skeleton";
import { OrdersParts } from "../OrdersParts/OrdersParts";
import { Sorting } from "../../../../../components/Lists/Sorting";
import MultioptionButton from "../../../../../components/Buttons/MultioptionButton";
import { Popover, Tooltip } from "@mui/material";
import {
  authorizeOrder,
  getGeneralTimeLine,
  getOrderGeneralInformation,
  getOrdersListParts,
  sentToVendorOrder,
  updateOrdersGeneralInformation,
} from "../../../Services/ordersServices";
import { environment } from "../../../../../environments/environment";
import Input from "../../../../../components/Inputs/Input";
import { useSnackBar } from "../../../../../hooks/useSnackBar";
import { usePermissions } from "../../../../../hooks/usePermissions";
import { formatAsUSCurrency } from "../../../../../utils/utils";
import { NewOrderContextProvider } from "../../../../SupplyChainModules/context/NewOrderContext";
import { ResultsNotFound } from "../../../../../components/ResultsNotFound/ResultsNotFound";
import { use } from "react";
import { OrderManagementContext } from "../../../context/OrderManagementContext";
import {
  localStorageGet,
  localStorageRemove,
  localStorageSave,
} from "../../../../../services/localStorageService";

// status mapping
// | Status             | abbreviation |
// | ------------------ | -------------------- |
// | Invoiced           | INV                  |
// | Partially Invoiced | P-INV                |
// | Received           | REC                  |
// | Pending Inspection | PI                   |
// | Order Discrepancy  | OD                   |
// | Partially Received | P-REC                |
// | Package Intake     | PKG-IN               |
// | Shipped            | SHP                  |
// | Partially Shipped  | P-SHP                |
// | Vendor Approved    | VA                   |
// | Sent To Vendor     | STV                  |
// | Authorized         | AUTH                 |
// | Released           | REL                  |
// | Cancelled          | CXL                  |
// | Generation         | GEN                  |

const timelineSteppersMap = [
  { id: 1, status: "Generation", date: "" },
  { id: 2, status: "Released", date: "" },
  { id: 3, status: "Authorized", date: "" },
  { id: 4, status: "Sent To Vendor", date: "" },
  { id: 5, status: "Vendor Approved", date: "" },
  { id: 6, status: "Partially Shipped", date: "" },
  { id: 7, status: "Shipped", date: "" },
  { id: 8, status: "Customs", date: "" },
  { id: 9, status: "Package Intake", date: "" },
  { id: 10, status: "Partially Received", date: "" },
  { id: 11, status: "Pending Inspection", date: "" },
  { id: 12, status: "Received", date: "" },
  { id: 13, status: "Invoiced", date: "" },
];
const orderManagementDraftPrefix = "supplyChain/orderManagement/draft/";
export const OrderCards = ({
  data,
  priceRange,
  isDraft,
  draftInfo = null,
  resetParentList,
}) => {
  const [timeLineData, setTimeLineData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(isDraft);
  const [edition, setEdition] = useState(false);
  const [labelEditingStatus, setLabelEditingStatus] = useState("Unsaved");
  const [viewTimeLine, setViewTimeline] = useState(false);
  const [viewPartList, setViewPartList] = useState(false);
  const [viewGeneral, setViewGeneral] = useState(false);

  const [companyDrop, setCompanyDrop] = useState(null);
  const [companyDropData, setCompanyDropData] = useState([]);
  const [shipViaVendorDrop, setShipViaVendorDrop] = useState(null);
  const [shipViaAccountDrop, setShipViaAccount] = useState(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const [partList, setPartList] = useState([]);

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(environment.ElemensPerPage);
  const [sortingOrder, setSortingOrder] = useState("id");
  const [generalInfoData, setGeneralInfoData] = useState(null);
  const [vendorsDrop, setVendorsDrop] = useState(null);
  const [locationsDrop, setLocationsDrop] = useState(null);
  const [sitesDrop, setSitesDrop] = useState(null);
  const [billToDrop, setBillTo] = useState(null);

  const [preselectVendor, setPreselectVendor] = useState(null);
  const [preselectLocation, setPreselectLocation] = useState(null);
  const [preselectSite, setPreselectSite] = useState(null);
  const [preselectBillTo, setPreselectBillTo] = useState(null);
  const [preselectShipViaVendor, setPreselectShipViaVendor] = useState(null);
  const [preselectShipViaAccount, setPreselectShipViaAccount] = useState(null);
  const [preselectCompany, setPreselectCompany] = useState(null);

  const [additionalFooterNotes, setAdditionalFooterNotes] = useState("");
  const [vendorSelected, setVendorSelected] = useState(null);
  const [locationSelected, setLocationSelected] = useState(null);
  const [siteSelected, setSiteSelected] = useState(null);
  const [billToSelected, setBillToSelected] = useState(null);
  const [companySelected, setCompanySelected] = useState(null);
  const [viaVendorSelected, setViaVendorSelected] = useState(null);
  const [viaAccountSelected, setViaAccountSelected] = useState(null);
  const [viaRemarkSelected, setViaRemarkSelected] = useState(null);
  const [version, setVersion] = useState(null);
  const { warningMsg, successMsg, infoMsg } = useSnackBar();
  const { hasPermission } = usePermissions();
  const [totalCost, setTotalCost] = useState(data?.total_cost);
  const { orderTotalCost, setOrderTotalCost, userApprovalRange } = useContext(
    OrderManagementContext
  );
  const [orderStatus, setOrderStatus] = useState(data.status);

  const mergePartList = (data) => {
    const merged = {};

    data.forEach((item) => {
      if (!merged[item.id]) {
        merged[item.id] = { ...item };
      } else {
        merged[item.id].quantity_order += item.quantity_order;

        merged[item.id].prn_count += item.prn_count;
        merged[item.id].wp_count += item.wp_count;
        merged[item.id].tc_fnd += item.tc_fnd;

        if (merged[item.id].prn !== item.prn) {
          if (Array.isArray(merged[item.id].prn)) {
            merged[item.id].prn.push(item.prn);
          } else {
            merged[item.id].prn = [merged[item.id].prn, item.prn];
          }
        }
      }
    });

    return Object.values(merged);
  };

  const getPartList = async (
    p = page,
    Ps = pageSize,
    search = "",
    order = sortingOrder,
    body = []
  ) => {
    const request = await getOrdersListParts(p, Ps, search, order, body);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      const mergedData = mergePartList(request.data.load);
      setPartList(mergedData);
    }
  };

  const openPopOver = (event) => {
    setAnchorEl({
      left: event.clientX,
      top: event.clientY,
    });
  };

  const handleClosepopOver = () => {
    setAnchorEl(null);
  };
  const getDropdownConfigCompany = async () => {
    let id = 61;
    const request = await dropdownConfig(id);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setCompanyDrop(request.data.load);
    }
  };
  const getDropdownConfigShipVendor = async () => {
    let id = 110;
    const request = await dropdownConfig(id);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setShipViaVendorDrop(request.data.load);
    }
  };
  const getDropdownConfigWShipAccount = async () => {
    let id = 111;
    const request = await dropdownConfig(id);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setShipViaAccount(request.data.load);
    }
  };

  const getDropdownConfigVendors = async () => {
    let id = 125;
    const request = await dropdownConfig(id);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setVendorsDrop(request.data.load);
    }
  };

  const getDropdownConfiLocation = async () => {
    let id = 17;
    const request = await dropdownConfig(id);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setLocationsDrop(request.data.load);
    }
  };

  const getDropdownConfigSites = async () => {
    let id = 19;
    const request = await dropdownConfig(id);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setSitesDrop(request.data.load);
    }
  };

  const getDropdownConfigBillTo = async () => {
    let id = 108;
    const request = await dropdownConfig(id);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setBillTo(request.data.load);
    }
  };

  const getAllDropdowns = async () => {
    const results = await Promise.all([
      getDropdownConfigCompany(),
      getDropdownConfigShipVendor(),
      getDropdownConfigWShipAccount(),
      getDropdownConfigVendors(),
      getDropdownConfiLocation(),
      getDropdownConfigSites(),
      getDropdownConfigBillTo(),
    ]);

    return results;
  };

  const getSortingChange = (data) => {
    setSortingOrder(data);
  };

  const getTimelineData = async () => {
    let body = [
      {
        columnName: "company_id",
        value: [parseInt(localStorage.getItem("u_company_id"))],
      },
      {
        columnName: "order_id",
        value: [data?.id],
      },
    ];

    const request = await getGeneralTimeLine(0, 200, "", "id", body);
    if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
      setTimeLineData(request.data.load);
    }
  };

  const mergeTimelineData = (staticSteps, backendSteps) => {
    return staticSteps.map((staticItem) => {
      // Find the matching backend step based on status
      const match = backendSteps.find(
        (b) => b.status.toLowerCase() == staticItem.status.toLowerCase()
      );
      return {
        ...staticItem,
        date: match?.date || "",
      };
    });
  };

  const mergedTimeline = useMemo(
    () => mergeTimelineData(timelineSteppersMap, timeLineData),
    [timeLineData]
  );

  const updateOrderGeneralData = async (type) => {
    if (type == "finish") {
      let request = await updateOrdersGeneralInformation(generalInfoData);

      if (request.data.responseCode == HTTP_RESPONSE_CODES.SUCCESS) {
        successMsg("Updated Succesfully", "Order Updated Successfully.");
        resetParentList(data.id);
        localStorageRemove(orderManagementDraftPrefix, { orderId: data.id });
        setEdition(false);
        setIsOpen(false);
        setDraft(false);
        setVersion(request.data.load.version);
      }
    } else {
      let request = await updateOrdersGeneralInformation(generalInfoData);

      if (request.data.responseCode == HTTP_RESPONSE_CODES.SUCCESS) {
        successMsg(
          "Updated Succesfully",
          "Order Updated Successfully , you can continue editing."
        );
        setDraft(true);
        setLabelEditingStatus("Editing");
        setVersion(request.data.load.version);
      }
    }
  };

  const cancelEdition = () => {
    try {
      resetParentList(data.id);
      localStorageRemove(orderManagementDraftPrefix, { orderId: data.id });
      setIsOpen(false);
      setDraft(false);
      setEdition(false);
      successMsg(
        "Edition Cancelled.",
        "The edition of the order has been cancelled successfully."
      );
    } catch (error) {
      console.error(error, "error canceling order");
    }
  };
  const multiOptionsBtn = [
    { label: "Save and Finish", icon: "icon-save", id: "save" },
    {
      label: "Save and Continue Editing",
      icon: "icon-save",
      id: "save-continue",
    },
    { label: "Cancel", icon: "icon-cross", id: "cancel", type: "danger" },
  ];

  const buttonsCallback = {
    save: () => updateOrderGeneralData("finish"),
    "save-continue": () => updateOrderGeneralData("continue"),
    cancel: () => {
      cancelEdition();
    },
  };

  const getGeneralInfo = async () => {
    if (draftInfo == null) {
      let request = await getOrderGeneralInformation(data?.id);
      if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
        setGeneralInfoData(request.data.load);
        setVersion(request.data.load.version);

        // preselection
        setPreselectVendor(parseInt(request.data.load?.vendor?.id));
        setPreselectLocation(parseInt(request.data.load?.locationId?.id));
        setPreselectSite(parseInt(request.data.load?.siteId?.id));
        setPreselectShipViaVendor(request.data.load.shipViaVendorId);
        setPreselectShipViaAccount(request.data.load.shipViaAccountId);
        setPreselectBillTo(parseInt(request.data.load.billTo?.id));
        setPreselectCompany(request.data.load.companyId);
        setAdditionalFooterNotes(request.data.load.footerNotes);
        setViaRemarkSelected(request.data.load.shipViaRemarks);

        // selection
        setVendorSelected(parseInt(request.data.load?.vendor?.id));
        setLocationSelected(parseInt(request.data.load?.locationId?.id));
        setSiteSelected(parseInt(request.data.load?.siteId?.id));
        setViaVendorSelected(request.data.load.shipViaVendorId);
        setViaAccountSelected(request.data.load.shipViaAccountId);
        setBillToSelected(parseInt(request.data.load.billTo?.id));
      }
    } else {
      preloadDataByDraft(draftInfo);
    }
  };

  const approveOrder = async () => {
    if (orderStatus === "Released") {
      let body = {
        id: data?.id,
      };
      let request = await authorizeOrder(body);
      if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
        successMsg(
          "Order Approved",
          "The order " +
            data.id +
            " has been successfully approved for a total amount of " +
            formatAsUSCurrency(data.total_cost) +
            "."
        );
        resetParentList();
      }
    }
  };

  const preloadDataByDraft = (data) => {
    setGeneralInfoData(data);
    setVersion(data.version);

    // preselection
    setPreselectVendor(parseInt(data?.vendor?.id));
    setPreselectLocation(parseInt(data?.locationId?.id));
    setPreselectSite(parseInt(data?.siteId?.id));
    setPreselectShipViaVendor(data.shipViaVendorId);
    setPreselectShipViaAccount(data.shipViaAccountId);
    setPreselectBillTo(parseInt(data.billTo?.id));
    setPreselectCompany(data.companyId);
    setAdditionalFooterNotes(data.footerNotes);
    setViaRemarkSelected(data.shipViaRemarks);

    // selection
    setVendorSelected(parseInt(data?.vendor?.id));
    setLocationSelected(parseInt(data?.locationId?.id));
    setSiteSelected(parseInt(data?.siteId?.id));
    setViaVendorSelected(data.shipViaVendorId);
    setViaAccountSelected(data.shipViaAccountId);
    setBillToSelected(parseInt(data.billTo?.id));
  };
  const saveDraft = () => {
    localStorageSave(
      orderManagementDraftPrefix,
      { orderId: data.id },
      generalInfoData
    );
    setDraft(true);
  };

  const sentOrderToVendor = async () => {
    if (orderStatus === "Authorized") {
      let body = {
        id: data?.id,
      };
      let request = await sentToVendorOrder(body);
      if (request.data.responseCode === HTTP_RESPONSE_CODES.SUCCESS) {
        successMsg(
          "Order Sent",
          "The order " + data.id + " has been successfully Sent To The Vendor."
        );
        setOrderStatus("Sent To Vendor");
      }
    }
  };
  useEffect(() => {
    setGeneralInfoData((prevData) => {
      return {
        ...prevData,
        version: version,
        vendor: { id: vendorSelected },
        locationId: {
          id: locationSelected,
        },
        siteId: {
          id: siteSelected,
        },
        billTo: {
          id: billToSelected,
        },
        shipViaVendorId: viaVendorSelected,
        shipViaAccountId: viaAccountSelected,
        shipViaRemarks: viaRemarkSelected,
        footerNotes: additionalFooterNotes,
      };
    });
  }, [
    vendorSelected,
    locationSelected,
    siteSelected,
    billToSelected,
    viaVendorSelected,
    viaAccountSelected,
    viaRemarkSelected,
    additionalFooterNotes,
  ]);

  useEffect(() => {
    if (isOpen && edition) {
      saveDraft();
      setLabelEditingStatus("Unsaved");
    }
  }, [generalInfoData]);

  useEffect(() => {
    if (!viewTimeLine && isOpen) {
      getAllDropdowns();
    }
  }, [isOpen]);

  useEffect(() => {
    setCompanyDropData(() => JSON.parse(localStorage.getItem("u_companies")));
  }, []);

  useEffect(() => {
    if (orderTotalCost?.orderId === data?.id) {
      setTotalCost(orderTotalCost?.totalCost || 0);
    }
  }, [orderTotalCost]);

  useEffect(() => {
    let body = [
      {
        columnName: "company_id",
        // value: [parseInt(localStorage.getItem("u_company_id"))],
        value: [parseInt(data?.company_id)],
      },
      {
        columnName: "parent_order_id",
        value: [data?.id],
      },
    ];

    viewPartList ? getPartList(0, pageSize, "", sortingOrder, body) : null;
  }, [sortingOrder]);

  const [billToDropParam, setBillToDropParam] = useState([
    {
      columnName: "accountId",
      value: parseInt(localStorage.getItem("u_account_id")),
    },
  ]);

  const isCostWithinRange = (data, priceRange) => {
    if (
      typeof data?.total_cost !== "number" ||
      typeof priceRange?.minRange !== "number" ||
      typeof priceRange?.maxRange !== "number"
    )
      return false;

    return (
      data.total_cost >= priceRange.minRange &&
      data.total_cost <= priceRange.maxRange
    );
  };

  const getApprovalTooltipText = (data, priceRange) => {
    if (orderStatus === "Released") {
      if (!isCostWithinRange(data, priceRange)) {
        return `You cannot approve this order because the price is outside your approval range, Min : ${formatAsUSCurrency(
          priceRange?.minRange
        )} - Max :  ${formatAsUSCurrency(priceRange?.maxRange)}`;
      }
      return null;
    }

    if (orderStatus === "Generation") {
      return "generation";
    }

    return "approved";
  };

  const updateDropdown = (dropdownId, parentValues, dependenciesToUpdate) => {
    let setDropdown;

    switch (dropdownId) {
      case 17:
        setDropdown = setLocationsDrop;
        break;
      case 19:
        setDropdown = setSitesDrop;
        break;
      case 110:
        setDropdown = setShipViaVendorDrop;
        break;
      case 111:
        setDropdown = setShipViaAccount;
        break;
      default:
        return;
    }

    const debounceUpdate = setTimeout(() => {
      setDropdown((prevState) => ({
        ...prevState,
        parentValues,
        dependenciesToUpdate,
        reload: true,
      }));
    }, 200);

    return () => clearTimeout(debounceUpdate);
  };

  const resetDropdownsReload = () => {
    setLocationsDrop((prevState) => ({ ...prevState, reload: false }));
    setSitesDrop((prevState) => ({ ...prevState, reload: false }));
    setShipViaVendorDrop((prevState) => ({ ...prevState, reload: false }));
    setShipViaAccount((prevState) => ({ ...prevState, reload: false }));
  };

  return (
    <section
      className="order-card"
      style={{
        height: isOpen && viewTimeLine ? "8rem" : "",
        background: isOpen && viewTimeLine ? "#EFFCFF" : "",
        border: isOpen && viewTimeLine ? "1px solid #B9D8DF" : "none",
      }}
      onContextMenu={(e) => {
        e.stopPropagation();
        e.preventDefault();
        openPopOver(e);
      }}
    >
      <Popover
        sx={{
          ".css-1dfcyzw-MuiPaper-root-MuiPopover-paper": {
            height: "auto",
            borderRadius: "var(--button-radius)",
            padding: "0",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          },
        }}
        id={id}
        open={open}
        onClose={handleClosepopOver}
        anchorReference="anchorPosition"
        anchorPosition={anchorEl}
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <div
          className="orders-card-general-info-context-menu view-info"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(null);
            if (!isOpen) {
              setIsOpen(true);
              setViewTimeline(false);
              setViewGeneral(true);
              getGeneralInfo();
            } else {
              setIsOpen(false);
              setViewTimeline((prev) => !prev);
              setViewGeneral((prev) => !prev);
            }
          }}
        >
          View General Information
        </div>
        {orderStatus === "Authorized" && (
          <div
            className="orders-card-general-info-context-menu sent-vendor"
            onClick={sentOrderToVendor}
          >
            Sent To Vendor
          </div>
        )}
        <div className="orders-card-general-info-context-menu cancel-order">
          Cancel Order
        </div>
      </Popover>

      <div
        className={`order-card-header ${isOpen ? "open" : ""}`}
        tabIndex={1}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
          setViewPartList(true);
          setViewTimeline(false);
          setViewGeneral(false);
          let body = [
            {
              columnName: "company_id",
              value: [parseInt(data?.company_id)],
            },
            {
              columnName: "parent_order_id",
              value: [data?.id],
            },
          ];

          getPartList(0, pageSize, "", sortingOrder, body);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            setViewPartList(true);
            setViewTimeline(false);
            setViewGeneral(false);
          }
        }}
        style={{
          background:
            (isOpen && viewTimeLine) || (isOpen && viewPartList)
              ? "#EFFCFF"
              : "#FBFBFB",
          border: isOpen && viewTimeLine ? "none" : "1px solid #F1F1F1",
        }}
      >
        {draft ? (
          <span className="order-management-editing-tag">
            {labelEditingStatus}
          </span>
        ) : (
          ""
        )}
        <Tooltip
          title={
            data?.order_type == "PO"
              ? "Purchase Order"
              : data?.order_type == "LO"
              ? "Loan Order"
              : data?.order_type == "SO"
              ? "Service Order"
              : data?.order_type == "RO"
              ? "Repair Order"
              : data?.order_type == "EO"
              ? "Exchange Order"
              : "Unknown Order Type"
          }
          placement="top"
        >
          <span className="order-card-type-order">
            {data?.order_type === "PO" ? (
              <i className="icon-cart_icon"></i>
            ) : data?.order_type === "LO" ? (
              <i className="icon-hands_greeting_icon"></i>
            ) : data?.order_type === "SO" ? (
              <i className="icon-service_icon"></i>
            ) : data?.order_type === "RO" ? (
              <i className="icon-tools_icon"></i>
            ) : data?.order_type === "EO" ? (
              <i className="icon-success"></i>
            ) : null}
            <p>{data?.order_type || "--"}</p>
          </span>
        </Tooltip>
        <Tooltip
          title={
            <div className="authorization-orders-tooltip-msg">
              <span>{orderStatus || "--"}</span>
              <p>Pending Level 3 Authorization</p>
            </div>
          }
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: "#29ABE2",
                color: "white",
                // minWidth : "8rem",
                maxWidth: "none",
                padding: " 0 .3rem",
              },
            },
          }}
          placement="top"
        >
          <span
            className="order-card-status-span"
            onClick={(e) => {
              e.stopPropagation();
              if (!isOpen) {
                getTimelineData();
                setIsOpen(true);
                setViewTimeline(true);
                setViewGeneral(false);
                setViewPartList(false);
              } else {
                setIsOpen(false);
                setViewGeneral(false);
                setViewPartList(false);
                setViewTimeline((prev) => !prev);
              }
            }}
          >
            {/* <div className="level-authorization-orders-dots level-auth-order-1"></div>
                        <div className="level-authorization-orders-dots level-auth-order-2"></div>
                        <div className="level-authorization-orders-dots level-auth-order-3"></div>
                        <i className='icon-inProgress_icon'></i> */}
            <p
              style={{
                color: "var(--blue-300)",
              }}
            >
              {{
                Invoiced: "INV",
                "Partially Invoiced": "P-INV",
                Received: "REC",
                "Pending Inspection": "PI",
                "Order Discrepancy": "OD",
                "Partially Received": "P-REC",
                "Package Intake": "PKG-IN",
                Shipped: "SHP",
                "Partially Shipped": "P-SHP",
                "Vendor Approved": "VA",
                "Sent To Vendor": "STV",
                Authorized: "AUTH",
                Released: "REL",
                Cancelled: "CXL",
                Generation: "GEN",
              }[orderStatus] || orderStatus}
            </p>
          </span>
        </Tooltip>

        <p className="ellipsis">{data?.id || "--"}</p>
        <p className="ellipsis">{data?.location_code || "--"}</p>
        <p className="ellipsis">{data?.site_name || "--"}</p>
        <p className="ellipsis">
          {data?.date_required
            ? new Date(data?.created_date)
                .toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")
            : "--"}
        </p>
        <p className="ellipsis">{data?.priority || "--"}</p>
        <p
          className="ellipsis"
          style={{
            color: "#29ABE2",
            textDecoration: "underline",
          }}
        >
          {data?.vendor_name || "--"}
        </p>
        <p
          className="ellipsis"
          style={{
            color: "#29ABE2",
            textDecoration: "underline",
          }}
        >
          {data?.supply_chain || "--"}
        </p>
        <p className="ellipsis">{data?.name || "--"}</p>

        <p className="ellipsis">
          {data?.created_date
            ? new Date(data?.created_date)
                .toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")
            : "--"}
        </p>
        {/* <Tooltip title={"Click To View General TimeLine"} arrow placement='top'> */}
        <p
          className="order-card-since-day"
          onClick={(e) => {
            e.stopPropagation();
            if (!isOpen) {
              setIsOpen(true);
              setViewTimeline(true);
              setViewGeneral(false);
              setViewPartList(false);
            } else {
              setIsOpen(false);
              setViewGeneral(false);
              setViewPartList(false);
              setViewTimeline((prev) => !prev);
            }
          }}
        >
          {data?.days_since !== undefined && data?.days_since !== null
            ? data.days_since === 0
              ? 0
              : data.days_since
            : "--"}
        </p>
        <p
          className="ellipsis"
          style={{
            //muestra el valor
            textAlign: "end",
            fontWeight: "600",
          }}
        >
          {formatAsUSCurrency(totalCost) + " " + data?.currency_name || "--"}
        </p>

        {/* </Tooltip> */}
        <Tooltip title="General Information" arrow placement="top">
          <p
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isOpen) {
                setIsOpen(true);
                setViewTimeline(false);
                setViewGeneral(true);
                getGeneralInfo();
              } else {
                setIsOpen(false);
                setViewTimeline((prev) => !prev);
                setViewGeneral((prev) => !prev);
              }
            }}
          >
            <i
              style={{
                fontSize: "1rem",
                color: "var(--black-mid)",
              }}
              className="icon-general_info"
            ></i>
          </p>
        </Tooltip>
        {
          <Tooltip
            title={getApprovalTooltipText(data, priceRange)}
            arrow
            placement="top"
          >
            <button
              disabled={
                !(
                  isCostWithinRange(data, priceRange) &&
                  orderStatus === "Released"
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                approveOrder();
              }}
              style={{
                background: "#00CE88",
                height: orderStatus == "Released" ? "1.5rem" : "1rem",
                width: orderStatus == "Released" ? "1.5rem" : "1rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: orderStatus == "Released" ? ".2rem" : "50%",
                fontSize: ".8rem",
                marginLeft: orderStatus == "Released" ? "0" : ".3rem",
                border: "none",
                position: "absolute",
                right: ".48rem",
              }}
            >
              {orderStatus == "Released" ? (
                <i
                  className="icon-approve_order"
                  style={{
                    color: "var(--white)",
                  }}
                ></i>
              ) : (
                <i
                  style={{
                    color: "var(--white)",
                  }}
                  className="icon-check-2"
                ></i>
              )}
            </button>
          </Tooltip>
        }
      </div>

      {viewTimeLine && isOpen && (
        <div className="order-card-timeLine-container">
          <TimeLineStepper
            data={mergedTimeline}
            label={"status"}
            connectorsColor={"#29ABE2"}
            bulletsColor={"#29ABE2"}
            activeStep={timeLineData.findIndex(
              (step) =>
                step.status.toLowerCase() ===
                timeLineData[timeLineData.length - 1].status.toLowerCase()
            )}
            activeBulletColor="#29ABE2"
            activeLabelColor="#29ABE2"
            secondLabelColumnName={"date"}
            showSecondLabel={true}
          />
        </div>
      )}

      {viewGeneral && isOpen ? (
        <section className={`order-card-content ${isOpen ? "open" : ""}`}>
          <div className="orders-card-general-information-cont">
            <div className="orders-cards-general-info-top">
              <span>
                <button>
                  <i className="icon-more-horizontal"></i>
                </button>
                <p>General</p>
              </span>
              <div className="orders-cards-general-info-status">
                <div className="orders-card-general-user-active">
                  <span>BB</span>
                  <p>Blair Blackmon - In Use</p>
                </div>
                <span
                  style={{
                    justifyContent: "end",
                  }}
                >
                  <p
                    style={{
                      fontSize: "calc(var(--p-size) + .1rem)",
                    }}
                  >
                    Authorized Status
                  </p>
                  <div>{orderStatus || "--"}</div>
                </span>
                {hasPermission("editGeneralInformation", data) ? (
                  edition ? (
                    <MultioptionButton
                      buttons={multiOptionsBtn}
                      buttonCallbacks={buttonsCallback}
                    ></MultioptionButton>
                  ) : (
                    <button
                      onClick={() => {
                        setEdition(true);
                        saveDraft();
                        setLabelEditingStatus("Editing");
                      }}
                      className="edit-order-general-info"
                    >
                      <i className="icon-edit"></i> Edit
                    </button>
                  )
                ) : null}
              </div>
            </div>
            <section className="orders-cards-general-info-bottom">
              <section className="orders-cards-payment-terms-cont">
                <div>
                  {companyDrop ? (
                    <Dropdowns
                      preSelectValue={preselectCompany}
                      staticDataColumnPreselection={"id"}
                      data={companyDrop}
                      isStatic={true}
                      staticData={companyDropData}
                      disabled={true}
                    ></Dropdowns>
                  ) : (
                    <Skeleton height={"2rem"}></Skeleton>
                  )}
                </div>
                <div>
                  {vendorsDrop ? (
                    <Dropdowns
                      data={vendorsDrop}
                      disabled={!hasPermission("editVendor", data) || !edition}
                      preSelectValue={preselectVendor}
                      onOptionSelect={(e) => {
                        setVendorSelected(e.id);
                      }}
                    ></Dropdowns>
                  ) : (
                    <Skeleton height={"2rem"}></Skeleton>
                  )}
                </div>
                <div
                  style={{
                    gap: "var(--gap)",
                  }}
                >
                  {locationsDrop ? (
                    <Dropdowns
                      onOptionSelect={(e) => {
                        setLocationSelected(e.id);
                        setPreselectSite(null);
                      }}
                      updateDropdown={updateDropdown}
                      resetDropdownsReload={resetDropdownsReload}
                      data={locationsDrop}
                      disabled={
                        !hasPermission("editLocation", data) || !edition
                      }
                      preSelectValue={preselectLocation}
                      // optionLabel={"code"}
                      optionsSelectedColumnName={"code"}
                    ></Dropdowns>
                  ) : (
                    <Skeleton height={"2rem"}></Skeleton>
                  )}

                  {sitesDrop ? (
                    <Dropdowns
                      updateDropdown={updateDropdown}
                      resetDropdownsReload={resetDropdownsReload}
                      data={sitesDrop}
                      disabled={!hasPermission("editSite", data) || !edition}
                      preSelectValue={preselectSite}
                      onOptionSelect={(e) => {
                        setSiteSelected(e.id);
                      }}
                      optionsSelectedColumnName={"site_code"}
                    ></Dropdowns>
                  ) : (
                    <Skeleton height={"2rem"}></Skeleton>
                  )}
                </div>
                <div
                  style={{
                    borderRadius: "var(--button-radius)",
                    background: "var(--white)",
                    border: "1px solid #EFEFEF",
                  }}
                >
                  {billToDrop ? (
                    <Dropdowns
                      onOptionSelect={(e) => {
                        setBillToSelected(e.id);
                      }}
                      data={billToDrop}
                      parameters={billToDropParam}
                      disabled={!hasPermission("editBillTo", data) || !edition}
                      preSelectValue={preselectBillTo}
                    ></Dropdowns>
                  ) : (
                    <Skeleton height={"2rem"}></Skeleton>
                  )}
                </div>
                <div
                  style={{
                    padding: "0 0 0 .4rem",
                  }}
                >
                  <p
                    style={{
                      color: "var(--black-mid)",
                      fontWeight: "500",
                    }}
                  >
                    Payment Terms
                  </p>
                  <button
                    className="paymetn-term-config-button"
                    style={{
                      color: "var(--white)",
                      padding: "0 .2rem",
                      fontSize: "var(--p-size)",
                      cursor: "pointer",
                      width: "10rem",
                    }}
                    disabled={
                      !hasPermission("paymentTermsConfiguration", data) ||
                      !edition
                    }
                  >
                    Payment Term Configuration
                  </button>
                </div>

                <section>
                  <p>
                    Full payment must be made in advance when placing the order.
                  </p>
                </section>

                <div className="legacy-reference-section">
                  <p>Legacy system Reference</p>
                  <span>{generalInfoData?.legacySystemReference || "--"}</span>
                </div>

                <div className="authorized-user-section">
                  <p>Authorized User</p>
                  <span>Michael Pardo</span>
                </div>
              </section>
              <section className="orders-cards-shipping-info">
                <button
                  disabled={
                    !hasPermission("termsAndConditions", data) || !edition
                  }
                >
                  Terms and Conditions
                </button>
                <button disabled={!hasPermission("history", data) || !edition}>
                  History
                </button>
                <h3>Shipping Information</h3>
                <section>
                  <div>
                    {shipViaVendorDrop ? (
                      <Dropdowns
                        updateDropdown={updateDropdown}
                        resetDropdownsReload={resetDropdownsReload}
                        data={shipViaVendorDrop}
                        disabled={
                          !hasPermission("editShipViaVendor", data) || !edition
                        }
                        preSelectValue={preselectShipViaVendor}
                        onOptionSelect={(e) => {
                          setViaVendorSelected(e.id);
                          setPreselectShipViaAccount(null);
                        }}
                      ></Dropdowns>
                    ) : (
                      <Skeleton height={"2rem"}></Skeleton>
                    )}
                  </div>
                  <div>
                    {shipViaAccountDrop ? (
                      <Dropdowns
                        updateDropdown={updateDropdown}
                        resetDropdownsReload={resetDropdownsReload}
                        data={shipViaAccountDrop}
                        disabled={
                          !hasPermission("editShipViaAccount", data) || !edition
                        }
                        preSelectValue={preselectShipViaAccount}
                        onOptionSelect={(e) => {
                          setViaAccountSelected(e.id);
                        }}
                      ></Dropdowns>
                    ) : (
                      <Skeleton height={"2rem"}></Skeleton>
                    )}
                  </div>

                  <div>
                    <Input
                      label={"Ship Via Remarks"}
                      disabled={
                        !hasPermission("editShipViaRemarks", data) || !edition
                      }
                      name={"shipViaRemarks"}
                      value={viaRemarkSelected}
                      onChange={(e) => {
                        setViaRemarkSelected(e.target.value);
                      }}
                    ></Input>
                  </div>
                </section>
              </section>
              <section className="orders-cards-orders-footers-cont">
                <section className="orders-footers-notes">
                  <div>Order Footer Notes</div>
                  <section>
                    {generalInfoData?.accountFooterNotes || "--"}
                  </section>
                </section>
                {/* <Input placeholder={"additional Footer Notes"} name={"footer notes"} label={"Additional Footer Notes"}></Input> */}
                <section className="orders-footers-notes-text-area-cont">
                  <div>Additional Footer Notes</div>
                  <textarea
                    placeholder="Add Additional Notes..."
                    value={additionalFooterNotes}
                    onChange={(e) => {
                      setAdditionalFooterNotes(e.target.value);
                    }}
                    disabled={
                      !hasPermission("editAdditionalFooterNotes", data) ||
                      !edition
                    }
                  ></textarea>
                </section>
              </section>
            </section>
          </div>
        </section>
      ) : (
        <></>
      )}
      {isOpen && viewPartList ? (
        <section className="order-card-part-list">
          <Sorting
            onOrderChange={getSortingChange}
            properties={{
              id: "Line Number",
              part_number: "PN",
              description: "Description",
              quantity_ordered: "QTY Ordered",
              quantity_recieved: "QTY Received",
              Category: "Category",
              sub_category: "Sub Category",
              part_request: "PRN Info",
              // aircraft: "AC",
              // wp: "WP",
              // tc_fnd: "TC / FND",
              shipping_date: "Estimated Shipping Date",
              status: "Status",
            }}
            displayButton={false}
          ></Sorting>
          <div>
            {partList.length > 0 ? (
              partList.map((part, index) => (
                <NewOrderContextProvider>
                  <OrdersParts
                    odOriginalCost={data?.total_cost}
                    key={index}
                    data={part}
                    orderId={data?.id}
                    currency_name={data?.currency_name}
                  ></OrdersParts>
                </NewOrderContextProvider>
              ))
            ) : (
              <ResultsNotFound></ResultsNotFound>
            )}
          </div>
        </section>
      ) : (
        <></>
      )}
    </section>
  );
};
